import { orgRef } from '@/lib/seo/graph';
import { INTRO_OFFERS_ENABLED } from '@/lib/introPassPath';
/**
 * Centralized SEO metadata for all public pages.
 * Titles <60 chars, descriptions <160 chars.
 * Voice: parent-language first ("child not confident", "child quiet in class")
 * then expert-language. Age band: 8-14 (Indian children).
 */
export const SEO = {
  home: {
    title: undefined, // Uses default full title from SEOHead
    description:
      "Psychologist-designed, story-based behavioural and life-skills courses for kids 8-14 in India: SEL, confidence and emotions.",
    canonical: '/',
  },
  learningJourneys: {
    title: 'My Learning Journeys',
    description:
      'Self-paced courses for kids aged 8-14: confidence, public speaking, communication, focus, resilience, emotional skills and friendship.',

    canonical: '/learning-journeys',
    ogImage: 'https://leadxkids.com/og-learning-journeys.jpg',
  },
  assessment: {
    title: 'LeadXkids Know Your Child Assessment',
    description:
      'Understand your child with the LeadXkids Know Your Child Assessment for ages 8-14, included with Annual Membership. Separate from our free confidence check.',
    canonical: '/assessment',
    ogImage: 'https://leadxkids.com/og-assessment.jpg',
  },
  assessmentSampleReport: {
    title: 'Sample Child Confidence & Personality Report',
    description:
      'See a sample confidence and personality report for kids: trait analysis, growth areas and practical next steps for parents.',
    canonical: '/assessment/sample-report',
    ogImage: 'https://leadxkids.com/og-assessment.jpg',
  },
  subscription: {
    title: 'Membership & Plans for Kids Life Skills Courses',
    description:
      'Affordable plans for life skills courses, confidence assessments and parent tools for children aged 8-14. No auto-renewal.',
    canonical: '/subscription',
    ogImage: 'https://leadxkids.com/og-subscription.jpg',
  },

  // Login page: no SEO value, keeps crawl budget on content pages.
  auth: {
    title: 'Sign In',
    description:
      "Sign in or create your LeadXkids account to start your child's confidence and life skills journey.",
    noindex: true,
  },
  install: {
    title: 'Install the App',
    description:
      'Install LeadXkids for quick access and offline learning. Works on Android, iOS and desktop.',
    canonical: '/install',
  },
  termsOfUse: {
    title: 'Terms of Use',
    description: 'Terms and conditions for using the LeadXkids platform and services.',
    canonical: '/terms-of-use',
  },
  refundPolicy: {
    title: 'Refund Policy',
    description: 'Refund and cancellation policy for LeadXkids subscriptions and purchases.',
    canonical: '/refund-policy',
  },
  copyrightPolicy: {
    title: 'Copyright Policy',
    description: 'Copyright and intellectual property information for LeadXkids content and materials.',
    canonical: '/copyright-policy',
  },
  privacyPolicy: {
    title: 'Privacy Policy',
    description: "How LeadXkids collects, uses and protects your and your child's personal information.",
    canonical: '/privacy-policy',
  },
  notFound: {
    title: 'Page Not Found',
    description: 'The page you are looking for does not exist.',
    noindex: true,
  },
  profile: {
    title: 'My Profile',
    description: 'Manage your LeadXkids profile, children and account settings.',
    noindex: true,
  },
  research: {
    title: 'Research on Children’s Life Skills',
    description:
      'Parent-friendly syntheses of external research on confidence, communication and life skills, with sources, population limits and practical context.',
    canonical: '/research',
  },
  courses: {
    title: 'Life Skills Courses for Kids 8-14',
    description:
      'Browse every LeadXkids course for children aged 8-14: confidence, communication, focus, emotional skills and daily habits.',
    canonical: '/courses',
  },
  glossary: {
    title: 'Glossary: Life Skills & SEL Terms',
    description:
      'Plain-English definitions of child confidence, life skills, growth mindset, SEL, resilience and other terms used across LeadXkids and child development.',
    canonical: '/glossary',
  },
} as const;

/**
 * FAQ structured data for the homepage — AEO-optimised
 * around the exact questions parents ask about child confidence,
 * communication, focus, resilience and SEL.
 */
export function buildHomepageFaqJsonLd(trialPrice: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Why is my child confident at home but quiet outside?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Children often feel safe expressing themselves at home but freeze in unfamiliar social settings. It's usually a confidence and social comfort gap, not a personality flaw. LeadXkids' story-based confidence course for kids aged 8-14 helps children rehearse social situations, build self-trust and start speaking up at school and in public.",
        },
      },
      {
        '@type': 'Question',
        name: 'How do I build confidence in my 8 to 14 year old child?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Confidence in children is built through small, repeated wins: speaking up, handling judgement, recovering from setbacks. LeadXkids offers a self-paced confidence building course for kids with practical activities parents can use alongside.',
        },
      },
      {
        '@type': 'Question',
        name: 'What are the signs of low confidence in children?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Common signs include: child does not speak up in class, child avoids new people, child gives up easily, child is afraid of being judged, or child is smart but doesn't participate. LeadXkids offers a free parent-guided confidence check for children aged 8-14.",
        },
      },
      {
        '@type': 'Question',
        name: 'How can I help my child speak up in class?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Public speaking and classroom participation are teachable skills. LeadXkids has a public speaking course for kids that helps children handle stage fear, structure their thoughts and speak confidently in front of teachers and peers.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can I improve focus and concentration in my child?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Focus is a skill, not a personality trait. LeadXkids has a focus and concentration course for kids that teaches age-appropriate techniques to reduce distraction while studying and build deep-work habits.',
        },
      },
      {
        '@type': 'Question',
        name: 'What should I do if my child gives up easily?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Children who give up easily usually need a growth mindset, not more pressure. LeadXkids has a resilience course for kids aged 8-14 that uses stories and reflection to help children handle failure and bounce back.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is social emotional learning (SEL) for kids?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Social emotional learning helps children recognise feelings, regulate emotions, build relationships and make responsible decisions. LeadXkids is a social emotional learning platform for Indian children that teaches SEL through story-based courses.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I help my shy child make friends?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Shy children benefit from rehearsed social scripts and small low-pressure social wins. LeadXkids includes friendship and social confidence units that help children start conversations, handle being left out and build peer connections.',
        },
      },
      {
        '@type': 'Question',
        name: 'What age group is LeadXkids designed for?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'LeadXkids is built for Indian children aged 8 to 14, with age-appropriate content for each level.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I try LeadXkids for free?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: INTRO_OFFERS_ENABLED
            ? `The confidence check for kids is free. You can also access all 20 courses with a 3-day all-access pass for Rs ${trialPrice}, with no auto-renewal, and then choose a quarterly or annual membership for continued access.`
            : 'The confidence check for kids is free. All 20 courses are included in the quarterly and annual memberships, which are one-time purchases with no auto-renewal.',
        },
      },
      ...learningJourneysFaqJsonLd.mainEntity,
    ],
  } as const;
}

/**
 * FAQ structured data for the confidence check page.
 */
export const confidenceCheckFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the LeadXkids confidence check for kids?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A free, 2-minute scenario-based confidence check for children aged 8-14. Parents answer how their child typically reacts in real situations (class, friends, judgement, setbacks) and get a personalised report across 4 dimensions of childhood confidence, plus a parent playbook.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does the confidence check take?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'About 2 minutes. It is parent-led, scenario-based and designed to be completed in one short sitting on any phone or laptop.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is the confidence check free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The confidence check, the personalised report and the parent playbook are all completely free. You do not need to pay or subscribe to receive the report.',
      },
    },
    {
      '@type': 'Question',
      name: 'What age group is the confidence check for?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It is designed for Indian children aged 8 to 14, with scenarios that reflect real Indian school, family and social situations.',
      },
    },
    {
      '@type': 'Question',
      name: 'What will the confidence report tell me?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The report scores your child across 4 confidence dimensions (speaking up and being seen, trying and bouncing back, social confidence and inner steadiness), highlights the strongest and weakest area, and gives concrete next steps for parents.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is this different from a personality test?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A personality test labels who a child is. The LeadXkids confidence check measures behaviours that can be built — speaking up, handling judgement, bouncing back — so parents get a clear action plan instead of a fixed label.',
      },
    },
  ],
};

/**
 * FAQ structured data for the subscription / pricing page.
 */
export const subscriptionFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is included in a LeadXkids membership?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A LeadXkids membership unlocks all 20 story-based life skills courses for children aged 8-14 — confidence, public speaking, communication, focus, resilience, emotional skills and friendship — plus the Know Your Child Assessment and the Parent Hub.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I try LeadXkids before choosing a membership?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: INTRO_OFFERS_ENABLED
          ? 'The confidence check for kids is free. The ₹99 3-day pass gives access to all 20 courses and does not auto-renew. Quarterly and annual memberships are separate purchases.'
          : 'The confidence check for kids is free. All 20 courses are included in the quarterly and annual memberships.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do LeadXkids plans auto-renew?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: INTRO_OFFERS_ENABLED
          ? 'No. The ₹99 pass, quarterly membership and annual membership are one-time purchases for their stated access periods and do not auto-renew.'
          : 'No. The quarterly membership and annual membership are one-time purchases for their stated access periods and do not auto-renew.',
      },
    },
    {
      '@type': 'Question',
      name: 'How are payments handled?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Payments are processed securely via Razorpay and support all major Indian payment methods including UPI, cards, net banking and wallets. Invoices are emailed automatically.',
      },
    },
  ],
};

/**
 * Course questions shared by the public homepage and catalogue metadata.
 */
export const learningJourneysFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What life skills courses are available for kids on LeadXkids?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'LeadXkids offers story-based courses for children aged 8-14 covering confidence, public speaking, communication, focus and concentration, resilience, emotional skills, friendship and personality development. Courses are self-paced and story-based.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does each course take to complete?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Each course is broken into short 5-10 minute story-based units that children can complete at their own pace. Most journeys are designed to be finished over 2-4 weeks with 10 minutes of practice a day.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are the courses suitable for shy or introverted children?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The courses are designed specifically with shy and introverted children in mind — they rehearse real social situations privately before trying them in real life, which is the safest way to build confidence.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do parents need to sit with the child during the courses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Courses are self-paced and child-friendly, so children aged 8-14 can do them independently. Parents get a separate dashboard to track progress and see reflections.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does the first course cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: INTRO_OFFERS_ENABLED
          ? 'The ₹99 3-day pass is a one-time purchase with access to all 20 courses and no auto-renewal. Quarterly and annual memberships are available for longer access.'
          : 'Courses are unlocked with a LeadXkids membership — a one-time quarterly or annual purchase with no auto-renewal. The confidence check is free.',
      },
    },
  ],
};

/**
 * Course JSON-LD for the courses catalogue.
 * Names LeadXkids as the provider — required by Google's Course rich-result spec.
 * `hasCourseInstance` keeps it valid without committing to specific dates.
 *
 * This lives on /subscription, not /learning-journeys: the journeys hub is a
 * signed-in-only page (noindex), and structured data on a noindex URL is never
 * used for a rich result.
 */
export function buildLearningJourneysCourseJsonLd(opts: {
  /** Lowest live membership price, in INR. Offer is omitted when unknown. */
  membershipPrice?: number | null;
  /** Canonical URL of the indexable page carrying this schema. */
  url?: string;
} = {}) {
  const membershipPrice = opts.membershipPrice ?? null;
  const pageUrl = opts.url ?? 'https://leadxkids.com/subscription';
  return {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'LeadXkids Life Skills Journey for Kids 8-14',
  description:
    'A self-paced library of story-based life skills courses for Indian children aged 8-14 — confidence, public speaking, communication, focus, resilience, emotional skills, friendship and personality development.',
  url: pageUrl,

  inLanguage: 'en-IN',
  educationalLevel: 'Ages 8-14',
  audience: {
    '@type': 'EducationalAudience',
    educationalRole: 'student',
    audienceType: 'Children aged 8-14',
  },
  about: [
    'Confidence building for children',
    'Public speaking for kids',
    'Communication skills',
    'Focus and concentration',
    'Resilience',
    'Emotional intelligence',
    'Friendship skills',
    'Personality development',
  ],
  provider: organizationRef,
  ...(membershipPrice === null
    ? {}
    : {
        offers: {
          '@type': 'Offer',
          category: 'Subscription',
          price: String(membershipPrice),
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          url: 'https://leadxkids.com/subscription',
        },
      }),
  hasCourseInstance: {
    '@type': 'CourseInstance',
    '@id': `${pageUrl}#course-instance`,
    courseMode: 'online',
    courseWorkload: 'PT10M',
    inLanguage: 'en-IN',
    courseSchedule: {
      '@type': 'Schedule',
      repeatFrequency: 'Daily',
      repeatCount: 30,
    },
  },
  };
}

/**
 * FAQ structured data for the /assessment page.
 */
export const assessmentFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the LeadXkids child personality assessment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It is an assessment for children aged 8-14 that combines parent input and child responses to produce a personality and confidence report covering traits, strengths, growth areas and parenting recommendations.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is this different from a typical personality test?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most online personality tests label a child as one fixed type. The LeadXkids assessment measures both stable traits and changeable behaviours, so parents get a growth plan, not a label.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does the assessment take?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The parent section takes about 5 minutes and the child section takes 10-15 minutes. Both can be paused and resumed later.',
      },
    },
    {
      '@type': 'Question',
      name: 'What does the assessment report include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The report includes a personality profile, confidence scores across 4 dimensions, strengths and growth areas, parent recommendations, and suggested courses tailored to your child.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I see a sample assessment report?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. A full sample assessment report is available at /assessment/sample-report so parents can see exactly what they receive.',
      },
    },
  ],
};

/**
 * Breadcrumb JSON-LD generator
 */
export function breadcrumbJsonLd(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://leadxkids.com${item.url}`,
    })),
  };
}

/**
 * The Organization entity now lives in src/lib/seo/graph.ts, which builds the
 * single per-route @graph. These re-exports keep page schema pointing at that
 * one `@id` instead of minting look-alike Organization objects.
 */
export { ORG_ID } from '@/lib/seo/graph';
export const organizationRef = orgRef;

// No hardcoded rupee defaults: a stale literal here publishes a price that
// disagrees with the admin-configured value. When a price cannot be resolved
// server-side the Offer is omitted instead of guessed.

interface PlanOfferInput {
  name: string;
  price: number;
  discounted_price?: number | null;
  duration_days?: number | null;
}

/**
 * Product + Offer structured data for the pricing page: the configured all-course
 * pass, the Know Your Child assessment, and every live membership plan.
 * Prices are passed in from the page so schema can never disagree with the UI.
 */
export function commerceJsonLd(opts: {
  trialPrice?: number;
  plans?: PlanOfferInput[];
}) {
  const trialPrice = opts.trialPrice ?? null;
  const plans = opts.plans ?? [];

  const offer = (price: number, url: string, name?: string) => ({
    '@type': 'Offer',
    ...(name ? { name } : {}),
    price: String(price),
    priceCurrency: 'INR',
    availability: 'https://schema.org/InStock',
    url: `https://leadxkids.com${url}`,
    seller: organizationRef,
  });

  const trial = trialPrice === null ? null : {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': 'https://leadxkids.com/subscription#all-access-pass',
    name: `LeadXkids 3-Day All-Access Pass (Rs ${trialPrice})`,
    description:
      `One-time Rs ${trialPrice} pass with 3 days of access to all 20 LeadXkids story-based life skills courses for children aged 8-14. No auto-renewal.`,
    brand: organizationRef,
    category: 'Online life skills course for kids',
    audience: {
      '@type': 'PeopleAudience',
      suggestedMinAge: 8,
      suggestedMaxAge: 14,
      geographicArea: { '@type': 'Country', name: 'India' },
    },
    offers: offer(trialPrice, '/subscription', '3-day all-course pass'),
  };


  const membership = plans.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': 'https://leadxkids.com/subscription#membership',
        name: 'LeadXkids Membership',
        description:
          'LeadXkids membership unlocks every story-based life skills journey for children aged 8-14, the Know Your Child assessment and the Parent Hub.',
        brand: organizationRef,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'INR',
          offerCount: plans.length,
          lowPrice: String(
            Math.min(...plans.map((p) => Number(p.discounted_price ?? p.price ?? 0))),
          ),
          highPrice: String(
            Math.max(...plans.map((p) => Number(p.discounted_price ?? p.price ?? 0))),
          ),
          offers: plans.map((p) =>
            offer(Number(p.discounted_price ?? p.price ?? 0), '/subscription', p.name),
          ),
        },
      }
    : null;

  return [trial, membership].filter(
    (node) => node !== null,
  ) as Record<string, unknown>[];
}
